import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, Avatar,
  CircularProgress, Button, alpha,
} from '@mui/material';
import { Close, PeopleAlt, CalendarToday, NavigateBefore, NavigateNext, Public } from '@mui/icons-material';
import { C } from './shared';
import { apiFetch } from '../../utils/api';

interface UserRow {
  id: number | string;
  name: string | null;
  email: string | null;
  created: string | null;
  reg_ip: string | null;
  reg_country: string | null;
  reg_city: string | null;
  reg_region: string | null;
  last_ip: string | null;
}
interface UsersResponse {
  success: boolean;
  filters: { created: string | null; startDate: string | null; endDate: string | null };
  todayCount: number;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  count: number;
  users: UserRow[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Filter query string (without leading '?') appended to the users API — e.g.
   * 'created=today' or 'startDate=2026-07-01&endDate=2026-07-11'. Empty string
   * means no date filter (all users). The parent owns the date semantics so the
   * modal count always matches the dashboard card that opened it.
   */
  filterQuery: string;
  /** Heading shown in the dialog (e.g. "All Users" / "Today's Users"). */
  title: string;
  /** Human-readable date shown in the header chip (e.g. "Today", "Jul 1 – Jul 11"). */
  dateLabel?: string | null;
}

const PAGE_SIZE = 20;

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const regLocation = (u: UserRow) =>
  [u.reg_city, u.reg_country].filter(Boolean).join(', ');

const initials = (name: string | null, email: string | null) => {
  const src = (name || email || '?').trim();
  return src.charAt(0).toUpperCase();
};

const UserListModal: React.FC<Props> = ({ open, onClose, filterQuery, title, dateLabel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<UsersResponse | null>(null);

  const fetchUsers = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);

    const url = `/api/admin-common/users?page=${targetPage}&limit=${PAGE_SIZE}${filterQuery ? `&${filterQuery}` : ''}`;

    // The upstream proxy occasionally resets an idle keep-alive connection
    // (net::ERR_CONNECTION_RESET). It's transient, so retry a few times with a
    // short backoff before surfacing an error to the user.
    const MAX_ATTEMPTS = 3;
    let lastErr: any = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        const res = await apiFetch<UsersResponse>(url);
        setData(res);
        setLoading(false);
        return;
      } catch (e: any) {
        lastErr = e;
        if (attempt < MAX_ATTEMPTS) {
          await new Promise((r) => setTimeout(r, attempt * 400));
        }
      }
    }

    setError(lastErr?.message || 'Failed to load users');
    setData(null);
    setLoading(false);
  }, [filterQuery]);

  // Reset to first page + reload whenever the dialog opens or the range changes.
  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchUsers(1);
  }, [open, fetchUsers]);

  const goToPage = (p: number) => {
    setPage(p);
    fetchUsers(p);
  };

  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.totalPages ?? 1;
  const users = data?.users ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: 3 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: alpha(C.primary, 0.15), color: C.primary, width: 36, height: 36 }}>
            <PeopleAlt sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ color: C.text, fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>{title}</Typography>
            <Typography sx={{ color: C.textMuted, fontSize: '0.72rem' }}>
              {loading ? 'Loading…' : `${total} user${total === 1 ? '' : 's'}`}
              {dateLabel && (
                <Box component="span" sx={{ ml: 0.75, display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                  <CalendarToday sx={{ fontSize: 11 }} />
                  {dateLabel}
                </Box>
              )}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: C.textSecondary, '&:hover': { color: C.text } }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={32} sx={{ color: C.primary }} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: C.error, fontSize: '0.85rem', mb: 1.5 }}>{error}</Typography>
            <Button size="small" variant="outlined" onClick={() => fetchUsers(page)} sx={{ textTransform: 'none', borderColor: C.border, color: C.text }}>
              Retry
            </Button>
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography sx={{ color: C.textMuted, fontSize: '0.85rem' }}>No users found</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 360 }}>
              <TableHead>
                <TableRow sx={{ '& th': { color: C.textMuted, fontSize: '0.7rem', fontWeight: 600, borderBottom: `1px solid ${C.border}`, textTransform: 'uppercase', letterSpacing: 0.3 } }}>
                  <TableCell>User</TableCell>
                  <TableCell>UID</TableCell>
                  <TableCell>Registration Location</TableCell>
                  <TableCell>Registration IP</TableCell>
                  <TableCell align="right">Joined</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} sx={{ '& td': { borderBottom: `1px solid ${alpha(C.border, 0.6)}`, py: 1.25 }, '&:hover': { bgcolor: C.cardHover } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: alpha(C.primary, 0.15), color: C.primaryLight }}>
                          {initials(u.name, u.email)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: C.text, fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {u.name || 'Unnamed'}
                          </Typography>
                          <Typography sx={{ color: C.textSecondary, fontSize: '0.7rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
                            {u.email || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: C.primaryLight, fontSize: '0.72rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {u.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>
                      {regLocation(u) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Public sx={{ fontSize: 13, color: C.textSecondary, flexShrink: 0 }} />
                          <Typography noWrap sx={{ color: C.text, fontSize: '0.75rem' }}>{regLocation(u)}</Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ color: C.textMuted, fontSize: '0.75rem' }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: u.reg_ip ? C.textSecondary : C.textMuted, fontSize: '0.72rem', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                        {u.reg_ip || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ color: C.textSecondary, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{fmtDate(u.created)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Pagination footer */}
        {!loading && !error && users.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderTop: `1px solid ${C.border}` }}>
            <Typography sx={{ color: C.textMuted, fontSize: '0.72rem' }}>
              Page {page} of {totalPages}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton size="small" disabled={page <= 1} onClick={() => goToPage(page - 1)} sx={{ color: C.textSecondary, border: `1px solid ${C.border}`, '&.Mui-disabled': { opacity: 0.4 } }}>
                <NavigateBefore fontSize="small" />
              </IconButton>
              <IconButton size="small" disabled={page >= totalPages} onClick={() => goToPage(page + 1)} sx={{ color: C.textSecondary, border: `1px solid ${C.border}`, '&.Mui-disabled': { opacity: 0.4 } }}>
                <NavigateNext fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserListModal;
